import { useCurrentEvent } from "queries/events"
import { useLanguageTemplates } from "queries/languageTemplates"
import { useAccountConfiguration } from "queries/users"
import { Translations } from "sharedTypes"

/**
 * Resolves the Translations that should be shown: the given template if set, else the
 * current event's default, else the plain English defaults (a null default_language_template_id
 * — on a brand-new event, or one that's never customized anything — means exactly that).
 *
 * A template's `translations` is a sparse, key-subset override — only the fields someone
 * actually customized are present — so this merges per-key onto the English defaults rather
 * than swapping in the whole object, mirroring the backend's LanguageTemplates::Resolver
 * (Forms::TranslationsPresenter#present does `defaults.deep_merge(@translations)`). Using the
 * template object wholesale would show blank text for every field the template hasn't
 * customized, even though guests would actually see the English default for it.
 */
const useResolvedTranslations = (templateId?: string | null): Translations => {
  const { data: event } = useCurrentEvent()
  const { data: templates = [] } = useLanguageTemplates(event.id)
  const { accountConfiguration } = useAccountConfiguration()

  const resolvedId = templateId ?? event.defaultLanguageTemplateId
  const template = resolvedId ? templates.find((t) => t.id === resolvedId) : undefined

  return { ...accountConfiguration.translations, ...template?.translations } as Translations
}

export default useResolvedTranslations
